// MarketingFooter.jsx
//
// Site-wide footer for the unauthenticated marketing pages. Four columns
// on desktop, stacked on mobile. Hash routes for in-app navigation, mailto
// for contact.

import React from 'react';
import { Mail, MapPin, Twitter, Linkedin } from 'lucide-react';
import { BRAND, goTo } from './marketingTokens';

const SUPPORT_EMAIL = 'hello@docketflowapp.com';

export default function MarketingFooter() {
  const year = new Date().getFullYear();

  const linkBtn = (label, onClick) => (
    <button
      onClick={onClick}
      className="text-sm transition-colors text-left"
      style={{ color: BRAND.textMuted }}
      onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.blueDeep; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
    >
      {label}
    </button>
  );

  return (
    <footer
      className="w-full mt-24 pt-16 pb-10 border-t"
      style={{ background: BRAND.bgLight, borderColor: BRAND.border }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Top: 4-column grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">

          {/* Brand block */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/logo-transparent.png" alt="DocketFlow" className="w-8 h-8 object-contain" />
              <span className="text-lg font-bold tracking-tight" style={{ color: BRAND.blueDeep }}>
                DocketFlow
              </span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: BRAND.textMuted }}>
              Practice management built for Ghanaian law firms. Case files, billing, trust
              accounting, secure messaging — everything you need, in cedis.
            </p>
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://twitter.com/" target="_blank" rel="noreferrer"
                className="p-2 rounded-lg transition-colors"
                style={{ color: BRAND.textMuted, background: BRAND.bgLighter }}
                aria-label="Twitter"
              >
                <Twitter size={16} />
              </a>
              <a
                href="https://linkedin.com/" target="_blank" rel="noreferrer"
                className="p-2 rounded-lg transition-colors"
                style={{ color: BRAND.textMuted, background: BRAND.bgLighter }}
                aria-label="LinkedIn"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: BRAND.textHead }}>
              Product
            </h4>
            <div className="flex flex-col gap-2.5">
              {linkBtn('Features',    () => goTo('features'))}
              {linkBtn('Pricing',     () => goTo('pricing'))}
              {linkBtn('Security',    () => goTo('security'))}
              {linkBtn('Book a demo', () => goTo('bookDemo'))}
              {linkBtn('Sign in',     () => goTo('login'))}
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: BRAND.textHead }}>
              Company
            </h4>
            <div className="flex flex-col gap-2.5">
              {linkBtn('About us', () => goTo('about'))}
              {linkBtn('Contact',  () => { window.location.href = `mailto:${SUPPORT_EMAIL}`; })}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: BRAND.textHead }}>
              Get in touch
            </h4>
            <div className="space-y-3">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex items-start gap-2 text-sm transition-colors"
                style={{ color: BRAND.textMuted }}
                onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.blueDeep; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
              >
                <Mail size={14} className="mt-0.5 shrink-0" />
                <span>{SUPPORT_EMAIL}</span>
              </a>
              <div className="flex items-start gap-2 text-sm" style={{ color: BRAND.textMuted }}>
                <MapPin size={14} className="mt-0.5 shrink-0" />
                <span>Accra, Ghana</span>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom: copyright + meta */}
        <div
          className="mt-12 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{ borderTop: `1px solid ${BRAND.border}` }}
        >
          <p className="text-xs" style={{ color: BRAND.textFaint }}>
            © {year} DocketFlow. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-xs" style={{ color: BRAND.textFaint }}>
            <span>Built in Ghana 🇬🇭</span>
            <span>·</span>
            <span>v1.0</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
